import { useProcess } from "@/contexts/ProcessContext"
import { useCreateAbsenceRequest } from "@/entities/absenceRequest/useAbsenceRequest"
import { zodResolver } from "@hookform/resolvers/zod"
import { parseISO, addHours, isBefore, startOfDay, endOfDay } from "date-fns"
import RecurringFields from "@/components/form/RecurringFields";
import { useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { z } from "zod"
import { InputFormControl } from "../../components/form/InputFormControl"
import { DateTimeSelector } from "@/components/form/DateTimeSelector"
import { isDevEnv } from "../../config/env"
import { absenceRequestSchema } from "../../entities/absenceRequest/absenceRequest"
import { uploadFile } from "../../firebase/storage"
import { useAuth } from "../../hooks/useAuth"
import { ShortNoticeDialog } from "@/components/ui/ShortNoticeDialog"
import { useTimeMachine } from "@/contexts/TimeMachineContext"
import { useCurrentSchoolYear } from "@/entities/schoolYear/useSchoolYear"
import { v4 } from "uuid"

const formSchema = absenceRequestSchema
   .extend({
      justification_doc_file: z
         .instanceof(FileList, { message: "Le justificatif est requis" })
         .optional(),
      recurring_end_type: z.enum(["school_year_end", "custom_date"]).optional(),
   })
   .refine((data) => data.to >= data.from, {
      message: "La date de fin doit être postérieure à la date de début",
      path: ["to"],
   })
   .refine((data) => {
      if (data.is_recurring && data.recurring_end_type === "custom_date") {
        return !!data.recurring_end_date;
      }
      return true;
    }, {
      message: "Veuillez sélectionner une date de fin de récurrence",
      path: ["recurring_end_date"],
   })

type FormData = z.infer<typeof formSchema>

export default function AbsenceRequestForm() {
   const { now } = useTimeMachine()
   const { user } = useAuth()
   const { startProcess } = useProcess()
   const {
      register,
      handleSubmit,
      formState: { errors },
      reset,
      setValue,
      watch,
   } = useForm<FormData>({
      resolver: zodResolver(formSchema),
      defaultValues: {
         young_id: user?.id || "",
         status: "pending",
         reason: "",
         ...(isDevEnv && {
            reason: "Test",
            // is_recurring: true,
         }),
      },
   })
   const createAbsenceRequest = useCreateAbsenceRequest()
   const navigate = useNavigate()
   const { data: currentSchoolYear } = useCurrentSchoolYear()

   useEffect(() => {
      setValue("young_id", user?.id || "")
   }, [user])

   // Gestion du popup short notice
   const [showShortNoticeDialog, setShowShortNoticeDialog] = useState(false)
   const pendingDataRef = useRef<FormData | null>(null)

   const submitAbsenceRequest = async (data: FormData) => {
		startProcess(async () => {
			if (data.justification_doc_file && data.justification_doc_file.length > 0) {
				const fileUrl = await uploadFile(data.justification_doc_file.item(0)!, user!.id)
				data.justification_doc = fileUrl
			}
         delete data.justification_doc_file
         
         if (!data.is_recurring) {
            delete data.recurring_period
            delete data.recurring_end_date
         } else {
            data.recurring_group = v4()
            if (data.recurring_end_type === "school_year_end") {
               data.recurring_end_date = currentSchoolYear?.end_date
            } else if (data.recurring_end_type === "custom_date") {
               data.recurring_end_date = endOfDay(data.recurring_end_date!)
            }
         }
         delete data.recurring_end_type

			const id = await createAbsenceRequest(data, { quickMode: true })
			reset()
			navigate(`/jeune/demande-absence/${id}`)
		})
	}

   const onSubmit = async (data: FormData) => {
		const now = new Date()
		const fromDate = typeof data.from === 'string' ? parseISO(data.from) : data.from
		const threshold = addHours(now, 48)
		if (isBefore(fromDate, threshold)) {
			pendingDataRef.current = data
			setShowShortNoticeDialog(true)
			return
		}
		submitAbsenceRequest(data)
	}

   return (
      <div className="page-section">
         <h1 className="title">Nouvelle demande d'absence</h1>
         <div className="">
            <div className=" ">
               <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="form-control">
                     <DateTimeSelector
                        label="Date de début"
                        initialDateValue={startOfDay(now)}
                        enabledTimeOptions={["start_of_day", "morning_break", "lunch_break", "afternoon_break", "specific_time"]}
                        onChange={(date: Date | null) => setValue("from", date ?? undefined as any)}
                        error={errors.from?.message}
                     />
                  </div>

                  <div className="form-control">
                     <DateTimeSelector
                        label="Date de fin"
                        initialDateValue={endOfDay(now)}
                        minDateValue={watch("from")}
                        enabledTimeOptions={["morning_break", "lunch_break", "afternoon_break", "end_of_day", "specific_time"]}
                        initialTimeOption="end_of_day"
                        onChange={(date: Date | null) => setValue("to", date ?? undefined as any)}
                        error={errors.to?.message}
                     />
                  </div>

                  <InputFormControl
                     label="Motif"
                     field="reason"
                     register={register}
                     errors={errors}
                  />

                  <div className="form-control">
                     <label className="label">
                        <span className="label-text">Justificatif</span>
                     </label>
                     <input
                        type="file"
                        className={`file-input file-input-bordered w-full ${
                           errors.justification_doc_file ? "input-error" : ""
                        }`}
                        accept=".pdf,.jpg,.jpeg,.png"
                        {...register("justification_doc_file")}
                     />
                     {errors.justification_doc_file && (
                        <div className="text-error mt-1">
                           {errors.justification_doc_file.message}
                        </div>
                     )}
                  </div>

                  <RecurringFields
                    register={register}
                    watch={watch}
                    errors={errors}
                  />

                  <button type="submit" className="btn btn-primary w-full">
                     Soumettre la demande
                  </button>
               </form>
               {showShortNoticeDialog && (
                  <ShortNoticeDialog
                     message={"Attention, ta demande est faite moins de 48h à l'avance. Nous nous réservons le droit de la refuser pour cette absence d'anticipation."}
                     onAcknowledge={() => {
                        setShowShortNoticeDialog(false)
                        if (pendingDataRef.current) {
                           submitAbsenceRequest(pendingDataRef.current)
                           pendingDataRef.current = null
                        }
                     }}
                  />
               )}
            </div>
         </div>
      </div>
   )
}
